/**
 * Selection Context
 *
 * Manages user's attribute selections, quantity, and validation.
 * Tracks all user choices and provides computed state for configuration completeness.
 *
 * @module contexts/selection
 */

import type { ConfiguratorVariation, StaffelprijsItem, DeliveryOption } from '../../../types';
import type { ProductDataContext } from './product-data.svelte';
import { __, sprintf } from '@wordpress/i18n';

/**
 * Creates reactive selection state
 *
 * Responsibilities:
 * - Track user's attribute selections
 * - Manage quantity selection (tier + custom)
 * - Validate selections and custom quantity
 * - Find matching variation based on selections
 *
 * @param productDataContext - The product data context for accessing variations
 * @returns Selection context with state and methods
 */
export function createSelectionContext(productDataContext: ProductDataContext) {
	// State
	let selections = $state<Record<string, string>>({});
	let selectedColors = $state<string[]>([]); // For balloon products multi-select
	let quantity = $state(0);
	let useCustomQuantity = $state(false);
	let customQuantity = $state(0);
	let customQuantityError = $state('');
	let deliveryOption = $state<DeliveryOption>('normaal');
	let selectedDruktype = $state('');
	let digitaleProef = $state(true); // true = met proef, false = zonder

	/**
	 * Derived: Check if all selections are complete
	 */
	const isComplete = $derived.by(() => {
		const attrs = productDataContext.attributeNames;
		const isBalloon = productDataContext.isBalloonProduct;

		if (isBalloon) {
			const colorAttrs = productDataContext.getAttributesByType('color');
			const hasColors = colorAttrs.length === 0 || selectedColors.length > 0;

			// Check non-color attributes
			const nonColorAttrs = attrs.filter((attrName) => {
				const attr = productDataContext.attributes.find((a) => (a.slug || a.name) === attrName);
				return attr?.type !== 'color';
			});
			const allNonColorSelected = nonColorAttrs.every((attrName) => {
				if (selections[attrName]) return true;
				const attrDef = productDataContext.attributes.find((a) => (a.slug || a.name) === attrName);
				if (attrDef && attrDef.options.length <= 1) return true;
				const options = getAvailableOptions(attrName);
				return options.length === 0;
			});

			return hasColors && allNonColorSelected && quantity > 0;
		}

		// Only require selection for attributes that are actually visible steps.
		// Hidden attributes (single static option or no available options) don't block completion.
		return attrs.every((attrName) => {
			if (selections[attrName]) return true;
			// Check if this attribute is hidden (single static option = auto-selected & hidden)
			const attrDef = productDataContext.attributes.find((a) => (a.slug || a.name) === attrName);
			if (attrDef && attrDef.options.length <= 1) return true;
			// If no dynamic options available, don't block completion
			const options = getAvailableOptions(attrName);
			return options.length === 0;
		}) && quantity > 0;
	});

	/**
	 * Derived: Get the matching variation based on current selections
	 */
	const currentVariation = $derived.by((): ConfiguratorVariation | null => {
		const variations = productDataContext.variations;
		if (!variations.length) return null;

		// For balloon products, include the first selected color in the matching
		const effectiveSelections = { ...selections };
		if (productDataContext.isBalloonProduct && selectedColors.length > 0) {
			const colorAttr = productDataContext.attributes.find(a => a.type === 'color');
			if (colorAttr) {
				effectiveSelections[colorAttr.slug] = selectedColors[0];
			}
		}

		// Find the most specific matching variation (prefer exact attribute matches over empty/wildcard)
		let bestMatch: ConfiguratorVariation | null = null;
		let bestScore = -1;
		const selectionEntries = Object.entries(effectiveSelections);

		for (const variation of variations) {
			const matches = selectionEntries.every(
				([key, value]) =>
					!value || !variation.attributes[key] || variation.attributes[key] === value
			);
			if (!matches) continue;

			// Score: count how many selected attributes have an exact match (non-empty) on the variation
			let score = 0;
			for (const [key, value] of selectionEntries) {
				if (value && variation.attributes[key] === value) {
					score++;
				}
			}

			if (score > bestScore) {
				bestScore = score;
				bestMatch = variation;
			}
		}

		return bestMatch;
	});

	/**
	 * Initialize selections with empty values for all attributes.
	 * Auto-selects attributes that have only one available option.
	 * Called when product data is loaded.
	 */
	function initializeSelections() {
		const attrs = productDataContext.attributeNames;
		attrs.forEach((attr) => {
			selections[attr] = '';
		});

		// Auto-select attributes with only 1 option
		attrs.forEach((attr) => {
			const options = getAvailableOptions(attr);
			if (options.length === 1) {
				selections[attr] = options[0];
			}
		});
	}

	/**
	 * Select an attribute value
	 *
	 * @param attributeName - The name of the attribute to set
	 * @param value - The selected value
	 */
	function selectAttribute(attributeName: string, value: string) {
		selections[attributeName] = value;
		// Reset quantity when selections change
		quantity = 0;
		useCustomQuantity = false;
		customQuantity = 0;
		customQuantityError = '';
		selectedDruktype = '';
	}

	/**
	 * Toggle a color selection (for multi-select balloon products)
	 *
	 * @param colorSlug - The color term slug to toggle
	 */
	function toggleColorSelection(colorSlug: string) {
		const index = selectedColors.indexOf(colorSlug);
		if (index > -1) {
			selectedColors = selectedColors.filter((slug) => slug !== colorSlug);
		} else {
			selectedColors = [...selectedColors, colorSlug];
		}
	}

	/**
	 * Clear all selected colors
	 */
	function clearColorSelections() {
		selectedColors = [];
	}

	/**
	 * Check if a color is selected
	 *
	 * @param colorSlug - The color term slug to check
	 * @returns True if the color is selected
	 */
	function isColorSelected(colorSlug: string): boolean {
		return selectedColors.includes(colorSlug);
	}

	/**
	 * Set quantity from a tier
	 *
	 * @param aantal - The quantity from the selected tier
	 * @param druktype - The print type of the selected tier (for disambiguation)
	 */
	function setQuantity(aantal: number, druktype: string = '') {
		useCustomQuantity = false;
		customQuantity = 0;
		customQuantityError = '';
		quantity = aantal;
		selectedDruktype = druktype;
		// Reset delivery option when quantity changes
		deliveryOption = 'normaal';
		digitaleProef = true;
	}

	/**
	 * Set quantity and delivery option atomically (used by OplageEnLevering table)
	 *
	 * @param aantal - The quantity to set
	 * @param option - The delivery option to set
	 * @param druktype - The print type of the selected tier (for disambiguation)
	 */
	function setQuantityAndDelivery(aantal: number, option: DeliveryOption, druktype: string = '') {
		useCustomQuantity = false;
		customQuantity = 0;
		customQuantityError = '';
		quantity = aantal;
		selectedDruktype = druktype;
		deliveryOption = option;
		// Set digitale proef based on delivery option
		if (option === 'spoed_3') {
			digitaleProef = false;
		} else if (option === 'normaal') {
			digitaleProef = true;
		} else {
			digitaleProef = true;
		}
	}

	/**
	 * Set delivery option (normaal or spoed variant)
	 *
	 * @param option - The delivery option to set
	 */
	function setDeliveryOption(option: DeliveryOption) {
		deliveryOption = option;
		// For 3-day delivery, force "zonder proef"
		if (option === 'spoed_3') {
			digitaleProef = false;
		} else if (option === 'normaal') {
			digitaleProef = true;
		} else {
			// For 5d/8d, default to "met proef"
			digitaleProef = true;
		}
	}

	/**
	 * Set digitale proef preference
	 *
	 * @param metProef - true for "met digitale proef", false for "zonder"
	 */
	function setDigitaleProef(metProef: boolean) {
		// For 3-day delivery, always force "zonder proef"
		if (deliveryOption === 'spoed_3') {
			digitaleProef = false;
			return;
		}
		digitaleProef = metProef;
	}

	/**
	 * Toggle custom quantity mode
	 */
	function toggleCustomQuantity() {
		useCustomQuantity = !useCustomQuantity;
		if (!useCustomQuantity) {
			customQuantity = 0;
			customQuantityError = '';
		} else {
			selectedDruktype = '';
		}
	}

	/**
	 * Validate custom quantity against available tiers
	 *
	 * @param value - The custom quantity value to validate
	 * @param availableTiers - Available tier options
	 * @returns true if valid, false otherwise
	 */
	function validateCustomQuantity(
		value: number,
		availableTiers: StaffelprijsItem[]
	): boolean {
		customQuantityError = '';

		if (value <= 0) {
			customQuantityError = __('Aantal moet groter zijn dan 0', 'wpc');
			return false;
		}

		if (availableTiers.length > 0) {
			const minQuantity = availableTiers[0].aantal;
			if (value < minQuantity) {
				customQuantityError = sprintf(__('Minimale afname is %d stuks', 'wpc'), minQuantity);
				return false;
			}
		}

		return true;
	}

	/**
	 * Apply custom quantity value
	 *
	 * @param value - The custom quantity to apply
	 * @param availableTiers - Available tier options for validation
	 */
	function applyCustomQuantity(
		value: number,
		availableTiers: StaffelprijsItem[]
	) {
		if (validateCustomQuantity(value, availableTiers)) {
			quantity = value;
		}
	}

	/**
	 * Reset all selections and quantities
	 */
	function reset() {
		selections = {};
		selectedColors = [];
		quantity = 0;
		useCustomQuantity = false;
		customQuantity = 0;
		customQuantityError = '';
		deliveryOption = 'normaal';
		selectedDruktype = '';
		digitaleProef = true;
		initializeSelections();
	}

	/**
	 * Get available options for a specific attribute
	 * Filters based on other current selections
	 *
	 * @param attributeName - The attribute to get options for
	 * @returns Array of available option values
	 */
	function getAvailableOptions(attributeName: string): string[] {
		const variations = productDataContext.variations;
		if (!variations.length) return [];

		// Get all variations that match current selections (excluding this attribute)
		const otherSelections = { ...selections };
		delete otherSelections[attributeName];

		const matchingVariations = variations.filter((variation) => {
			return Object.entries(otherSelections).every(
				([key, value]) =>
					!value || !variation.attributes[key] || variation.attributes[key] === value
			);
		});

		// Extract unique values for this attribute
		const values = new Set<string>();
		for (const variation of matchingVariations) {
			const value = variation.attributes[attributeName];
			if (value) values.add(value);
		}

		return Array.from(values).sort();
	}

	return {
		// State
		get selections() {
			return selections;
		},
		get selectedColors() {
			return selectedColors;
		},
		set selectedColors(value: string[]) {
			selectedColors = value;
		},
		get quantity() {
			return quantity;
		},
		get useCustomQuantity() {
			return useCustomQuantity;
		},
		get customQuantity() {
			return customQuantity;
		},
		set customQuantity(value: number) {
			customQuantity = value;
		},
		get customQuantityError() {
			return customQuantityError;
		},
		get deliveryOption() {
			return deliveryOption;
		},
		get selectedDruktype() {
			return selectedDruktype;
		},
		get digitaleProef() {
			return digitaleProef;
		},

		// Derived (use getters to access $derived values)
		get isComplete() {
			return isComplete;
		},
		get currentVariation() {
			return currentVariation;
		},

		// Methods
		initializeSelections,
		selectAttribute,
		toggleColorSelection,
		clearColorSelections,
		isColorSelected,
		setQuantity,
		toggleCustomQuantity,
		validateCustomQuantity,
		applyCustomQuantity,
		reset,
		getAvailableOptions,
		setQuantityAndDelivery,
		setDeliveryOption,
		setDigitaleProef,
	};
}

/**
 * Type for selection context
 */
export type SelectionContext = ReturnType<typeof createSelectionContext>;
